<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Hooks
	import { beforeUpdate } from 'svelte';

	//Services
	import { format } from 'svelte-i18n';

	//Helpers
	import { capFirstChar, colorLimited, percent_from_value_range } from '../../../helpers/utilities';
	import {
		handleCloudLocalLists,
		localGroupRequest,
		cloudGroupRequestV2,
		getDevicesFormatted,
	} from './control_group_helpers';
	import {
		get_gen_1_path_from_category,
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
		is_gen_2,
		real_id,
	} from 'src/helpers/interface_helpers';

	//Stores
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';

	//Design system components
	import { Slider, SliderV2, toast } from '@Shelly/shelly-design-system';

	//Types
	import type { Device } from '../../../types/Device';
	import { get_local_status } from 'src/services/local_requests';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let white_channel_support_lights = [];
	export let type: 'horizontal' | 'vertical' | 'simple' = 'horizontal';

	let range = [0, 255];
	let local = {};
	let cloud = {};

	$: memberStatusStore = createMemberStatusSnapshot(white_channel_support_lights.map((d) => d.id));
	$: memberSettingsStore = createMemberSettingsSnapshot(white_channel_support_lights.map((d) => d.id));
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	$: disabled = white_channel_support_lights.every(
		(device) => !memberStatus?.[real_id(device.id)]?.online,
	);

	$: firstLight = white_channel_support_lights[0];
	$: firstLightRealId = real_id(firstLight?.id);
	$: mode = get_mode(firstLight, memberStatus?.[firstLightRealId], memberSettings?.[firstLightRealId]);
	let loading = false;
	$: gen1_path = get_gen_1_path_from_category(firstLight);
	$: gen2_path = get_gen_2_component_channel_structure(firstLight);

	$: value =
		typeof memberStatus?.[firstLightRealId]?.[gen1_path] !== 'undefined'
			? +percent_from_value_range(
					memberStatus[firstLightRealId]?.[gen1_path]?.[firstLight?.channel]?.white,
					range,
				).toFixed(0)
			: typeof memberStatus?.[firstLightRealId]?.[gen2_path]?.white !== 'undefined'
				? +percent_from_value_range(
						memberStatus[firstLightRealId]?.[`${mode}:${firstLight?.channel}`]?.white,
						range,
					).toFixed(0)
				: 0;

	beforeUpdate(() => {
		let lists = handleCloudLocalLists(
			Array.from(Object.values(white_channel_support_lights).map((d: Device) => d.id)),
		);
		[cloud, local] = [lists.cloud, lists.local];
	});

	const sendRequest = async (e) => {
		loading = true;
		let whiteValue = e?.detail?.value;
		let data = { white: colorLimited(whiteValue, 'from') };
		if (Object.keys(cloud).length > 0) {
			let tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			const res = await cloudGroupRequestV2(`/v2/devices/set/groups`, tmpDevices, data);

			if (typeof res !== 'undefined' && Object.keys(res?.failedCommands || {}).length === 0) {
				value = whiteValue;
			}
			loading = false;
		}
		if (Object.keys(local).length > 0) {
			for (const id in local) {
				let res;
				if (is_gen_2($devices[id])) {
					//supports plus rgbw for now
					// data.white = colorLimited(data.white, 'to');
					res = await localGroupRequest(
						local[id],
						data,
						`${get_gen_2_component_from_category($devices[id])}.set`,
					);
				} else {
					res = await localGroupRequest(local[id], data, 'post');
				}
				if (res.isok) {
					const status_req = await get_local_status($devices[id], $devices[id].ip);
					let newStatus = {
						...memberStatus[$devices[id].id],
						...status_req,
					};
					devices_status.update_device_status({
						id: $devices[id].id,
						msg: newStatus,
					});
					await update_settings($devices[id]);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
			loading = false;
		}
	};

	const a11yProps = {
		ariaLabel: $format('label_white_channel'),
		ariaLabelValue: `${value}%`,
		...(type === 'vertical' ? { ariaLabelValue2: 'vertical' } : {}),
	};
</script>

{#if $showNewLayout}
	<SliderV2
		id="slider-p41i7pr"
		{a11yProps}
		class={type === 'vertical' ? 'flex-full-height mb-l' : ' mb-m'}
		title={capFirstChar($format('label_white_channel'))}
		{value}
		isLoading={loading}
		{type}
		on:value_change={async (e) => await sendRequest(e)}
		{disabled}
	/>
{:else}
	<Slider
		id="slider-p41i7pr"
		{a11yProps}
		class={type === 'vertical' ? 'flex-full-height mb-l' : ' mb-m'}
		title={capFirstChar($format('label_white_channel'))}
		{value}
		isLoading={loading}
		{type}
		on:value_change={async (e) => await sendRequest(e)}
		{disabled}
	/>
{/if}
